import ExcelJS from 'exceljs';
import { organizeHierarchy } from './tools_helper';
import { WriteItemParams, WriteItemResult, ProcessPalletParams, ProcessPalletResult } from './packingListTypes/types';

const generateHeaderConfiguration = (level: any,CFs:any) => {

    const headers = [
        "UPC",
        "Part Number",
        ...(CFs && CFs.show_ean  === "true" ? ["Ean Code"] : []),
        "Description",
        // "Pcs x Ctn", 
        // "Total Ctn", 
        ...(CFs && CFs.show_style == "true" ? ["Style"] : []),
        ...(CFs && CFs.show_size == "true" ? ["Size"] : []),
        ...(CFs && CFs.show_color  == "true" ? ["Color"] : []),
        ...(CFs && CFs.show_season  == "true" ? ["Season"] : []),
        "Quantity",
        "Weight",
        "Dimension",
        "CBM",
    ];

    const dynamicHeaders = Array.from({ length: level - 1 }, (_, i) => `dynamic${i + 1}`);
    const allHeaders = [...dynamicHeaders, ...headers];
    
    const orderHeaders: any = {};
    let column = 0;

    allHeaders.forEach(header => {
        const startCol = String.fromCharCode(65 + column); 
        if (header === "Dimension") {
            orderHeaders[header] = `${startCol}8:${String.fromCharCode(65 + column + 2)}8`; 
            orderHeaders["Length"] = `${startCol}9`;
            orderHeaders["Width"] = `${String.fromCharCode(65 + column + 1)}9`;
            orderHeaders["Height"] = `${String.fromCharCode(65 + column + 2)}9`;
            column += 3; 
        } else {
            orderHeaders[header] = `${startCol}8:${startCol}9`;
            column++;
        }
    });

    const lastColLetter = String.fromCharCode(65 + column - 1); // Última columna
    return {
        orderHeaders,
        mergeTitle: `A1:${lastColLetter}1`,
        lastCell: `${lastColLetter}1`,
        lastColumnNumber: column,
        positionLogo: `${String.fromCharCode(65 + column - 3)}3`, 
        positionEmail: `${String.fromCharCode(65 + column - 2)}7` 
    };
};

async function getBase64FromImageURL(url: string): Promise<string> {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
    }

    const blob = await response.blob();
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
        reader.onload = () => resolve((reader.result as string).split(',')[1]); // Retorna la parte base64
        reader.onerror = () => reject(new Error('Error'));
        reader.readAsDataURL(blob);
    });
}

const styleHeaders = {
    border: {
        top: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" },
        bottom: { style: "thin" },
    },
    font: { bold: true, size: 11, color: { argb: 'FFFFFF' } },
    alignment: { horizontal: 'center', vertical: 'middle' },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '0e113e' } },
};
const styleTitle = {
    font: { bold: true, size: 26, color: { argb: 'FFFFFF' } },
    alignment: { horizontal: 'center', vertical: 'middle' },
    fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '0e113e' } },
};
async function addLogo(sheet: any, coordinates: string) {
    const getAuthUser = localStorage.getItem('authUser');
    let user;
    if (getAuthUser) {
        user = JSON.parse(getAuthUser);
    }
    try {
        const logoPath = "/dosan.png";//user.NETWORK_ID == 36451 ? '/dosan.png': '/innobo.png';

        // Convertir la imagen a base64
        const logoBase64 = await getBase64FromImageURL(logoPath);
        if (logoBase64) {
            const imageId = sheet.workbook.addImage({
                base64: logoBase64,
                extension: 'png',
            });

            // Coordenadas para agregar la imagen
            const column = coordinates.charCodeAt(0) - 'A'.charCodeAt(0);
            const row = parseInt(coordinates.slice(1), 10) - 1;
            // height: user.NETWORK_ID == 36451 ? 50:100
            sheet.addImage(imageId, {
                tl: { col: column, row: row },
                ext: { width: 200, height: 50 },
                editAs: 'oneCell',
            });

        }
    } catch (error) {
        console.error('Error adding logo to sheet:', error);
    }
}


function verificarNivel(objetos: any) {
    let nivel = 1; // Nivel inicial
    const paquetes: Set<string> = new Set(); // Conjunto para almacenar paquetes únicos

    function calcularNivel(obj: any, nivelActual: number): number {
        if (obj.CLPackageName) {
            paquetes.add(obj.CLPackageName); // Agregar el paquete al conjunto
        }

        if (!obj.children || obj.children.length === 0) {
            return nivelActual; // No tiene hijos, se queda en el nivel actual
        }

        // Hay hijos, incrementamos el nivel
        let maxNivel = nivelActual + 1;
        obj.children.forEach((child: any) => {
            maxNivel = Math.max(maxNivel, calcularNivel(child, nivelActual + 1));
        });

        return maxNivel;
    }

    objetos.forEach((obj: any) => {
        nivel = Math.max(nivel, calcularNivel(obj, 1));
    });

    // Determinar la descripción del nivel máximo
    let nivelDescripcion;
    if (nivel === 1) nivelDescripcion = 1;
    if (nivel === 2) nivelDescripcion = 2;
    if (nivel === 3) nivelDescripcion = 3;
    if (nivel === 4) nivelDescripcion = 4;

    // Retornar el nivel máximo y los paquetes únicos
    return {
        typeOfCr: nivelDescripcion,
        dynamicColumns: Array.from(paquetes), // Convertir el conjunto en un array
    };
}


async function setHeader(sheet: any, objData: any, options: any = {},configuration:any) {
    const {
        typeOfCr = 1, // "2 Levels", "1 Level", or others
        nameColumn = [],
        styleArray1 = {},
        styleArrayFS = {},
        isPage2 = false, // To toggle between the main and secondary header
    } = options;

    // Helper to merge cells and set values
    const mergeAndSet = (cellRange: any, value: any, style = {}) => {
        sheet.mergeCells(cellRange);
        sheet.getCell(cellRange.split(":")[0]).value = value;
        if (Object.keys(style).length > 0) {
            sheet.getCell(cellRange.split(":")[0]).style = style;
        }
    };

    const keys = ["UPC", "Part Number","Ean Code","Description","Style","Size","Color","Season","Quantity", "Weight", "Dimension", "Length", "Width", "Height", "CBM", "dynamic1", "dynamic2", "dynamic"]
    const headersPage1: any = [];

    let countHeader = 0;

    for (let i = 0; i <= keys.length - 1; i++) {
        if (keys[i].includes("dynamic")) {
            if (configuration.orderHeaders[keys[i]]) {
                headersPage1.push([`${configuration.orderHeaders[keys[i]]}`,(nameColumn[countHeader] ? nameColumn[countHeader]:"BOX")])
                countHeader++;
            }
        } else {
            if(configuration.orderHeaders[keys[i]]){
                headersPage1.push([`${configuration.orderHeaders[keys[i]]}`, keys[i]])
            }
        }
    }

    // Common Header Setup
    mergeAndSet(isPage2 ? "A1:H1" : configuration.mergeTitle, "PACKING LIST", { ...styleArrayFS });

    // Common Data Rows
    sheet.getCell("A3").value = "Date:";
    sheet.getCell("B3").value = objData.date;
    sheet.getCell("A4").value = "Customer:";
    sheet.getCell("B4").value = objData.customer;
    sheet.getCell("A5").value = "Invoice N°";
    sheet.getCell("B5").value = objData.invoiceN;
    sheet.getCell("A6").value = "Brand:";
    sheet.getCell("B6").value = objData.brand;

    // Special rows for specific customers or data
    if (!isPage2 && objData.customer === "GABE'S" && objData.SupplierPONumber) {
        sheet.getCell("A7").value = "P.O Number:";
        sheet.getCell("B7").value = objData.SupplierPONumber;
    }

    // Header Columns
    const headers: [string, string][] = isPage2
        ? [
            ["A8", "Pallet #"],
            ["B8", "Description"],
            ["C8", "Cartons"],
            ["D8", "Total Weight Kg"],
            ["E8:G8", "Dimension cm"],
            ["E9", "Length"],
            ["F9", "Width"],
            ["G9", "Height"],
            ["H8", "CBM"],
        ]
        : headersPage1;

    headers.forEach(([range, text]) => mergeAndSet(range, text));

    // Column Width Adjustments
    const columnWidths = isPage2
        ? { A: 11, B: 20, C: 20, D: 20, E: 15, F: 15, G: 15, H: 15 }
        : { A: 16, B: 18, C: 18, D: 18, E: 16, F: 16, G: 16, H: 10, I: 10, J: 10, K: 10, L: 10 };

    Object.entries(columnWidths).forEach(([col, width]) => {
        sheet.getColumn(col).width = width;
    });

    const positionLogo = isPage2 ? "H3" : configuration.positionLogo;

    // Adjust column visibility for specific brands
    // if (!["LEGO", "GOLI"].includes(objData.brand?.toUpperCase())) {
    //     sheet.getColumn("E").width = 0;
    //     if (isPage2) {
    //         sheet.getColumn("F").width = 0;
    //         sheet.getColumn("G").width = 0;
    //     } else {
    //         sheet.getColumn("F").width = 0;
    //     }
    // }

    await addLogo(sheet, positionLogo);

    // Hyperlink
    const positionEmail = isPage2 ? "H7" : configuration.positionEmail;
    sheet.getCell(positionEmail).value = {
        text: "dosancargo.com",
        hyperlink: "https://www.dosancargo.com/",
    };

    // Optional styling for ranges
    // if (isPage2) {
    // Definir el rango de celdas (A9:H10)
    const startRow = 8;
    const endRow = isPage2 ? 8 : 9;
    const startCol = 1; // A = 1
    const endCol = isPage2 ? 8 : configuration.lastColumnNumber; // H = 8

    for (let row = startRow; row <= endRow; row++) {
        for (let col = startCol; col <= endCol; col++) {
            const cell = sheet.getCell(row, col);
            // Aplicar estilos a cada celda
            cell.style = {
                ...styleArray1
            };
        }
    }

    const startRow2 = 2; // A1 empieza en la fila 1
    const endRow2 = 7; // Hasta la fila 9
    const startCol2 = 1; // Columna A
    const endCol2 = isPage2 ? 8 : configuration.lastColumnNumber; // Columna L (12 es el índice de L)

    for (let row = startRow2; row <= endRow2; row++) {
        for (let col = startCol2; col <= endCol2; col++) {
            const cell = sheet.getCell(row, col);
            // Estilo de borde blanco
            cell.style = {
                border: {
                    top: { style: "thin", color: { argb: "FFFFFFFF" } }, // Blanco
                    left: { style: "thin", color: { argb: "FFFFFFFF" } }, // Blanco
                    bottom: { style: "thin", color: { argb: "FFFFFFFF" } }, // Blanco
                    right: { style: "thin", color: { argb: "FFFFFFFF" } }, // Blanco
                },
            };
        }
    }
}

//--------------------------------SUM---------------------------------

function sumOfColumns(sheet: any, initialRow: any, finalRow: any, col: any) {
    let sum = 0;
    for (let row = initialRow; row <= finalRow; row++) {
        sheet.getRow(row).height = 25;
        const cellValue = Number(sheet.getCell(`${col}${row}`).value);
        if (typeof cellValue === 'number') {
            sum += cellValue;
        }
    }

    const resultCell = sheet.getCell(`${col}${finalRow + 1}`);
    resultCell.value = sum;
    resultCell.alignment = { horizontal: 'center', vertical: 'middle' }; // Centrar la celda
}

async function setSum(sheet: any, type: any, row: any, styleHeaders: any, itemsOneLevel: any,configuration:any) {

    sheet.getRow(row).height = 15;
    sheet.getCell(`A${row}`).value = itemsOneLevel;

    const headers = ["Quantity","Weight", "Length", "Width", "Height", "CBM"];
    for (let i = 0; i <= headers.length - 1; i++) {
        const col = configuration.orderHeaders[headers[i]].charAt(0);
        sumOfColumns(sheet, 10, row - 1, col);
    }

    //----------------------styles----------------------------
    const startRow = row;
    const endRow = row;
    const startCol = 1; // A = 1
    const endCol = configuration.lastColumnNumber; // H = 8

    for (let rowIndex = startRow; rowIndex <= endRow; rowIndex++) {
        for (let colIndex = startCol; colIndex <= endCol; colIndex++) {
            const cell = sheet.getCell(rowIndex, colIndex);
            // Aplicar estilos a cada celda
            cell.style = {
                ...styleHeaders
            };
        }
    }
}

//------------------------------------------------------------
function f_getCustomFieldsValuesArr(obj: any = {}) {
    const result: any = {};

    if (obj.customFields) {
        const customFields = obj.customFields;
        // Case: Multiple CustomFields (array of fields)

        customFields.forEach((customField: any) => {
            const internalName: string = customField.internalName;
            const value = customField.value || '';
            result[internalName] = value;
        });
    }

    return result;
}

//-------------------------Get data---------------------------
const getDataFromProduct = (product: any, brand: any) => {
    const result: any = {};
    result.partNumber = product?.CLPartNumber || '';
    result.description = product?.CLDescription || '';
    result.quantity = product?.CLPieces || '';
    result.PalletID = product?.CLPalletID ? ` - ${product.CLPalletID}` : '';
    result.UPC = product?.SkUpc || '';
    result.PackageType = product?.CLPackageName || '';

    //-------------CFS-----------------

    result.eanCode = "end_code"
    result.style = "style";
    result.size = "size";
    result.color =  "color";
    result.season = "Season";



    //--------------------------------

    result.pcsXCtn = 0;
    result.totalCtn = 0;
    if (product?.customFields) {
        const customFields = f_getCustomFieldsValuesArr(product); // Assume this function is defined elsewhere
        // result.pcsXCtn = customFields?.ctnsitem || 0;
        // result.totalCtn = result.pcsXCtn * result.quantity;
        result.eanCode = customFields.ean_code || '';
        result.style = customFields.style  || '';
        result.size = customFields.sizes  || '';
        result.color =  customFields.color  || '';
        result.season = customFields.season  || '';

    }

    // Adjust quantities for specific brands
    const upperBrand = brand?.toUpperCase();
    if (upperBrand === 'LEGO' || upperBrand === 'GOLI') {
        const temp = result.quantity;
        result.quantity = result.totalCtn;
        result.totalCtn = temp;
    }

    return result;
};
//----------------------------DRAW ROW-----------------------------------
function drawRows(sheet: any, objData: any, fila: any, type: any,configuration:any) {
    const headers = ["UPC", "Part Number","Ean Code","Description","Style","Size","Color","Season","Quantity"]
    const keys = ["UPC", "partNumber","eanCode","description","style","size","color","season","quantity"]
    for (let i = 0; i <= headers.length - 1; i++) {
        if(configuration.orderHeaders[headers[i]]){
            const col = configuration.orderHeaders[headers[i]].charAt(0);
            sheet.getCell(`${col}` + fila).value = objData[keys[i]];
            if (keys[i] == "description") {
                sheet.getCell(`${col}` + fila).alignment = { wrapText: true };
            }
        }
    }
}
function drawRowsContainer(sheet: any, objData: any, initialRow: number, finalRow: number, type: any, level: number,configuration:any) {
    let firstColumn = "A" + initialRow;
    if (type === 1) {
        // result = printItemsInExcelCase1(data, organizedItems, worksheet, brand, typeOfCr);
    } else if (type === 2) {
        //si es dos niveles, la columna iniical es..
        firstColumn = "A" + initialRow;
    } else if (type === 3) {
        //si es tres niveles, la columna iniical es..
        if (level == 2) {
            firstColumn = "B" + initialRow;
        } else if (level == 1) {
            firstColumn = "A" + initialRow;
        }
    }
    const valueCotainer = objData.PalletID ? objData.PalletID:objData.PackageType;
    sheet.getCell(`${firstColumn}`).value = valueCotainer;
    sheet.getCell(`${firstColumn}`).alignment = { horizontal: 'center', vertical: 'middle' };

    mergeCells(sheet, initialRow, finalRow, [`${firstColumn.charAt(0)}`]);
}
//-------------------------------------------------------------
function getDataFromBox(box: any) {
    return {
        Weight: parseFloat(parseFloat(box.CLWeight || 0).toFixed(2)),
        Length: parseFloat(parseFloat(box.CLLength || 0).toFixed(2)),
        Width: parseFloat(parseFloat(box.CLWidth || 0).toFixed(2)),
        Height: parseFloat(parseFloat(box.CLHeight || 0).toFixed(2)),
        Volume: parseFloat(parseFloat(box.CLVolume || 0).toFixed(2)),
        PalletID: box.PalletID ? ` - ${box.PalletID}` : '',
    };
}

function mergeCells(sheet: any, filaInicial: number, filaFinal: number, columnas: any) {
    try {
        columnas.forEach((columna: any) => {
            const rango = `${columna}${filaInicial}:${columna}${filaFinal}`;
            sheet.mergeCells(rango);
        });    
    } catch (error) {
        console.log(" There was an error!!! in Merge CELLS");
        console.log(error)
        console.log(`filaInicial: ${filaInicial}, filaFinal: ${filaFinal}, columns: ${columnas}`);
    }
}

//--------------------------PRINT ITEMS 1-------------------------------
function processPallet({
    pallet,
    level = 0,
    sheet,
    currentRow,
    typeOfCr,
    itemCounter,
    brand,
    configuration
}: ProcessPalletParams): ProcessPalletResult {
    level += 1;
    // Validate required parameters
    if (!pallet || !sheet) {
        throw new Error("Invalid arguments: 'pallet' and 'sheet' are required.");
    }
    if (!pallet.children || pallet.children.length === 0) {
        const updated = writeItem({
            item: pallet,
            sheet,
            currentRow,
            typeOfCr,
            itemCounter,
            brand,
            level,
            configuration
        });
        return updated;
    } else {
        for (const child of pallet.children) {
            const updated = processPallet({
                pallet: child,
                level: level,
                sheet,
                currentRow,
                typeOfCr,
                itemCounter,
                brand,
                configuration
            });
            currentRow = updated.currentRow;
            itemCounter = updated.itemCounter;
        }
        //Print the father! 
        const updated = writeItemContainer({
            item: pallet,
            sheet,
            currentRow,
            typeOfCr,
            itemCounter,
            brand,
            level,
            configuration
        });
        return updated;
    }
    return { currentRow, itemCounter };
}

function writeItem({
    item,
    sheet,
    currentRow,
    typeOfCr,
    itemCounter,
    brand,
    level,
    configuration
}: WriteItemParams): WriteItemResult {
    if (!item || !sheet) {
        throw new Error("Invalid arguments: 'item' and 'sheet' are required.");
    }
    const itemData = getDataFromProduct(item, brand);
    drawRows(sheet, itemData, currentRow, typeOfCr,configuration);
    currentRow++;
    itemCounter++;
    return { currentRow, itemCounter };
}

// Created to print only items like carton and Pallets.
function writeItemContainer({
    item,
    sheet,
    currentRow,
    typeOfCr,
    itemCounter,
    brand,
    level,
    configuration
}: WriteItemParams): WriteItemResult {
    if (!item || !sheet) {
        throw new Error("Invalid arguments: 'item' and 'sheet' are required.");
    }
    const itemData = getDataFromProduct(item, brand);
    let startContainer = (currentRow - item.children.length);
    if (typeOfCr === 3 && level == 1 ) {
        const  numberNestedChildren = countNestedChildren(item);
        startContainer = (currentRow - numberNestedChildren);
    }    
    drawRowsContainer(sheet, itemData, startContainer, currentRow - 1, typeOfCr, level,configuration);
    itemCounter++;
    return { currentRow, itemCounter };
}

function countNestedChildren(father: any): number {
    if (!father.children || father.children.length === 0) {
        return 1;
    }
    let count = 0;
    for (const child of father.children) {
        count += countNestedChildren(child);
    }
    return count;
}

async function printItemsInExcelCase1(dataJson: any, organizedItems: any, sheet: any, brand: any, typeOfCr: any,configuration:any) {
    const inicio = 10;
    let currentRow = 10;
    let colCont1 = 1;
    let itemCont = 0;
    let col1 = inicio;
    if (organizedItems) {
        let itemCounter = 0;
        for (const pallet of organizedItems) {
            ({ currentRow, itemCounter } = processPallet({
                pallet,
                level: 0,
                sheet,
                currentRow,
                typeOfCr,
                itemCounter,
                brand,
                configuration
            }));
            const dimentions: any = getDataFromBox(pallet);
            const headers = ["Weight", "Length", "Width", "Height", "CBM"]
            const keys = ["Weight", "Length", "Width", "Height", "Volume"]
            for (let i = 0; i <= headers.length - 1; i++) {
                const col = configuration.orderHeaders[headers[i]].charAt(0);
                sheet.getCell(`${col}` + col1).value = Number(dimentions[keys[i]]);
                sheet.getCell(`${col}${col1}`).alignment = { horizontal: 'center', vertical: 'middle' };
            }
            //MergeLast Cells
            const col = configuration.orderHeaders[headers[0]].charAt(0);
            const arrayLetters = generateColumnsFromLetter(col);
            mergeCells(sheet, col1, currentRow - 1, arrayLetters);
            colCont1++;
            itemCont++;
            col1 = currentRow;

            
        }
    }
    return {
        col1,
        colCont1,
        itemCont
    };
}

function generateColumnsFromLetter(startLetter: string) {
    const columns = [];
    const letterCode = startLetter.charCodeAt(0);
    for (let i = 0; i < 5; i++) {
        columns.push(String.fromCharCode(letterCode + i));
    }
    return columns;
}

//-------------------------------------------------------------------

export async function descargarExcelDesdeJSON(data: any,groupName: string) {
    const { OperationData, comodityData } = data;
    if (!OperationData) {
        console.log("No se encontraron Datos para OperationData");
    }

    const number = OperationData?.Number || '';

    if (!comodityData) {
        console.log("No se encontraron Items");
        return "No se encontraron Items";
    }

    // const typeOfCr = GetCasePL(comodityData);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet 1');
    const worksheet2 = workbook.addWorksheet('Sheet 2');
    const organizedItems = organizeHierarchy(comodityData);
    const {typeOfCr,dynamicColumns} = verificarNivel(organizedItems);

    const objData: any = {
        date: new Date(OperationData.CreatedOn).toLocaleDateString('en-US'),
        customer: OperationData.ReleasedToName || '',
        SupplierPONumber: OperationData.SupplierPONumber || '',
    };
    // const isCustomerGabes = objData.customer === "GABE'S";
    let brand = '';
    let invoiceN = 'invoiceN';
    let po = '';
    let boxcf:any = {};

    if (OperationData.customFields) {
        boxcf = f_getCustomFieldsValuesArr(OperationData);
        console.log(boxcf,"boxcf");
        brand = boxcf.brand_auto || '-';
        // eslint-disable-next-line no-useless-escape
        invoiceN = groupName == "cargoReleaseViewList" ? (boxcf.so_number?.replace(/[\/:<>|"]/g, '') || 'invoiceN'):boxcf.so_number?.replace(/[\/:<>|"]/g, '') || "invoiceN"; 
        po = boxcf.purchaseorder || '';
    }

    const configuration = generateHeaderConfiguration(typeOfCr,boxcf);


    // const nameColumn = getNameFirstColumn(data, brand);
    objData.invoiceN = invoiceN;
    objData.brand = brand;
    objData.po = po;

    workbook.creator = 'INNOBO';
    workbook.lastModifiedBy = 'INNOBO';
    workbook.created = new Date();
    workbook.modified = new Date();


    const optionsPage1 = {
        typeOfCr: typeOfCr, // "2 Levels", "1 Level", or others
        nameColumn: dynamicColumns,
        styleArray1: styleHeaders,
        styleArrayFS: styleTitle,
        isPage2: false, // To toggle between the main and secondary header
    }

    const optionsPage2 = {
        typeOfCr: typeOfCr, // "2 Levels", "1 Level", or others
        nameColumn: dynamicColumns,
        styleArray1: styleHeaders,
        styleArrayFS: styleTitle,
        isPage2: true, // To toggle between the main and secondary header
    }

    await setHeader(worksheet, objData, optionsPage1,configuration);
    await setHeader(worksheet2, objData, optionsPage2,configuration);
    worksheet.name = invoiceN;
    //----------------- print data--------------------------------------
    const {col1,colCont1,itemCont} = await  printItemsInExcelCase1(data, organizedItems, worksheet, brand, typeOfCr,configuration);
    await setSum(worksheet, typeOfCr,col1,styleHeaders,organizedItems.length,configuration);
    //-------------------------------------------------------------
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/octet-stream' });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${number}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}




